import React from 'react';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import SmartRetry from './SmartRetry';

/**
 * ScanFailureActions - A component that provides intelligent retry options for failed scans
 * 
 * This component automatically detects the failure phase and shows appropriate retry options.
 * Can be embedded in scan overview pages, dashboards, or any scan management interface.
 */
export const ScanFailureActions = ({
	scan,
	onRetryStarted,
	onRetryComplete,
	className = ""
}) => {
	const isFailed = scan && ['failed', 'completed_with_errors'].includes(scan.status);

	if (!scan || !isFailed) {
		return null;
	}

	const scanId = scan.scanId || scan._id;

	const handleRetryStarted = (phase) => {
			if (onRetryStarted) {
			onRetryStarted(scanId, phase);
		}
	};

	const handleRetryComplete = (result) => {
		if (onRetryComplete) {
			onRetryComplete(scanId, result);
		}
	};

	const handleRetryError = (error) => {
		// Error is handled within SmartRetry component
	};

	return (
		<div className={`space-y-4 ${className}`}>
			{/* Scan Status Context */}
			<div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-500/10 dark:border-red-500/30">
				<div className="flex items-center space-x-2 text-red-800">
					<ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
					<div>
						<span className="font-medium">
							Scan {scan.status === 'failed' ? 'Failed' : 'Completed with Errors'}
						</span>
						{scan.errorMessage && (
							<p className="text-sm text-red-700 mt-1">{scan.errorMessage}</p>
						)}
					</div>
				</div>
			</div>

			{/* Smart Retry Component */}
			<SmartRetry
				scanId={scan.scanId || scan._id}
				onRetryStarted={handleRetryStarted}
				onRetryComplete={handleRetryComplete}
				onError={handleRetryError}
				showDetails={true}
			/>
		</div>
	);
};

/**
 * Usage Examples:
 * 
 * // In a scan overview page
 * <ScanFailureActions 
 *   scan={scanData} 
 *   onRetryStarted={(scanId, phase) => {
 *     showNotification(`Starting ${phase} retry...`);
 *     navigate('/scan-progress', { state: { scanId } });
 *   }}
 *   onRetryComplete={(scanId, result) => {
 *     showNotification(`Retry successful: ${result.message}`);
 *     refreshScanData();
 *   }}
 * />
 * 
 * // In a dashboard with multiple scans
 * {failedScans.map(scan => (
 *   <ScanFailureActions key={scan._id} scan={scan} />
 * ))}
 * 
 * // Minimal usage
 * <ScanFailureActions scan={scan} />
 */

export default ScanFailureActions;
